import React, { useState, useEffect } from "react";
import { Modal } from "./ui";
import client from "../lib/api";
import { toast } from "sonner";
import { num, money } from "../lib/holdingsMath";
import { PiggyBank, Plus, Trash2, Loader2, ArrowDownToLine, ArrowUpFromLine, ChevronDown, ChevronUp } from "lucide-react";

// Portfolio-wide committed / called / remaining / distributed summary. Renders nothing when the
// owner has no private-fund commitments on record. Reads the same roll-up as the Net-Worth PDF.
export function CommitmentsSummary() {
  const [t, setT] = useState(null);
  const [rows, setRows] = useState([]);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    client.get("/networth-statement").then(({ data }) => {
      const tot = data.commitments_totals;
      if (tot && tot.commitment > 0) { setT({ ...tot, count: (data.commitments || []).length }); setRows(data.commitments || []); }
    }).catch(() => {});
  }, []);
  if (!t) return null;
  const pct = t.commitment ? Math.min(100, Math.round((t.called / t.commitment) * 100)) : 0;
  const Cell = ({ label, val, tone }) => (
    <div><div className="text-[9px] uppercase tracking-wide opacity-55">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${tone || ""}`}>{val}</div></div>
  );
  return (
    <div className="mb-3 rounded-xl border border-black/10 bg-white/70 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid="commitments-summary">
      <button type="button" onClick={() => setOpenList((v) => !v)} className="mb-2 flex w-full items-center gap-2 text-[11px] font-semibold uppercase tracking-wide opacity-70 hover:opacity-100" data-testid="commitments-summary-toggle">
        <PiggyBank size={14} /> Capital commitments · {t.count} fund{t.count === 1 ? "" : "s"}
        {openList ? <ChevronUp size={14} className="ms-auto" /> : <ChevronDown size={14} className="ms-auto" />}
      </button>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Cell label="Committed" val={money(t.commitment)} />
        <Cell label="Called to date" val={money(t.called)} tone="text-amber-600" />
        <Cell label="Remaining" val={money(t.remaining)} tone="text-[var(--primary)]" />
        <Cell label="Distributed" val={money(t.distributed)} tone="text-emerald-600" />
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/10 dark:bg-white/10" data-testid="commitments-summary-bar">
        <div className="h-full rounded-full bg-[var(--primary)] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[10px] opacity-55">{pct}% of committed capital called across the portfolio.</div>
      {openList && (
        <div className="mt-3 overflow-x-auto rounded-md border border-black/10 dark:border-white/10" data-testid="commitments-breakdown">
          <table className="w-full text-[12px]">
            <thead className="bg-black/5 dark:bg-white/10"><tr>
              <th className="px-2 py-1.5 text-start font-medium">Fund</th>
              <th className="px-2 py-1.5 text-end font-medium">Committed</th>
              <th className="px-2 py-1.5 text-end font-medium">Called</th>
              <th className="px-2 py-1.5 text-end font-medium">Remaining</th>
              <th className="px-2 py-1.5 text-end font-medium">Distributed</th>
              <th className="px-2 py-1.5 text-end font-medium">% called</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-black/5 dark:border-white/5" data-testid={`commitments-row-${i}`}>
                  <td className="px-2 py-1.5">{r.name}<span className="ms-1 opacity-45">· {r.category}</span></td>
                  <td className="px-2 py-1.5 text-end tabular-nums">{money(r.commitment)}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums text-amber-600">{money(r.called)}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums text-[var(--primary)]">{money(r.remaining)}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums text-emerald-600">{money(r.distributed)}</td>
                  <td className="px-2 py-1.5 text-end tabular-nums">{r.pct_called}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const II = "w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[12px] dark:border-white/15 dark:bg-black/30";
const LL = "text-[9px] uppercase tracking-wide opacity-55";
const today = () => new Date().toISOString().slice(0, 10);

function Stat({ label, value, tone }) {
  return (
    <div className="rounded border border-black/10 bg-black/[0.03] px-2 py-1.5 dark:border-white/10 dark:bg-white/[0.04]">
      <div className={LL}>{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${tone || ""}`}>{value}</div>
    </div>
  );
}

// Committed vs. called vs. remaining capital for a private fund, with dated capital-call and
// distribution events. Honest arithmetic only (no waterfall/reconciliation engine). Holdings only.
export function CommitmentButton({ holding, reload }) {
  const id = holding.id;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [commitment, setCommitment] = useState(String(holding.commitment ?? ""));
  const [calls, setCalls] = useState(holding.capital_calls || []);
  const [dists, setDists] = useState(holding.distributions || []);
  const [draft, setDraft] = useState({ call: { date: today(), amount: "", note: "" }, distribution: { date: today(), amount: "", note: "" } });

  const called = calls.reduce((s, c) => s + num(c.amount), 0);
  const distributed = dists.reduce((s, d) => s + num(d.amount), 0);
  const commit = num(commitment);
  const remaining = commit ? Math.max(commit - called, 0) : 0;
  const pct = commit ? Math.min(100, Math.round((called / commit) * 100)) : 0;

  const openModal = () => {
    setCommitment(String(holding.commitment ?? ""));
    setCalls(holding.capital_calls || []);
    setDists(holding.distributions || []);
    setDraft({ call: { date: today(), amount: "", note: "" }, distribution: { date: today(), amount: "", note: "" } });
    setOpen(true);
  };

  const saveCommitment = async () => {
    setBusy(true);
    try { await client.put(`/holdings/${id}`, { data: { commitment: num(commitment) } }); reload && reload(); }
    catch (e) { toast.error("Could not save commitment."); }
    setBusy(false);
  };

  const addEvent = async (kind) => {
    const d = draft[kind];
    if (!d.date) return toast.error("Pick a date.");
    setBusy(true);
    try {
      const { data } = await client.post(`/holdings/${id}/commitment-events/${kind}`, { date: d.date, amount: num(d.amount), note: d.note });
      setCalls(data.capital_calls || []); setDists(data.distributions || []);
      setDraft((p) => ({ ...p, [kind]: { date: today(), amount: "", note: "" } }));
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add."); }
    setBusy(false);
  };

  const delEvent = async (kind, eid) => {
    setBusy(true);
    try {
      const { data } = await client.delete(`/holdings/${id}/commitment-events/${kind}/${eid}`);
      setCalls(data.capital_calls || []); setDists(data.distributions || []);
      reload && reload();
    } catch (e) { toast.error("Could not remove."); }
    setBusy(false);
  };

  const EventBlock = ({ kind, list, title, icon: Icon, tone }) => (
    <div>
      <div className={`mb-1 flex items-center gap-1 text-[11px] font-semibold ${tone}`}><Icon size={13} /> {title}</div>
      <div className="mb-2 grid grid-cols-4 gap-1">
        <input type="date" className={II} value={draft[kind].date} onChange={(e) => setDraft((p) => ({ ...p, [kind]: { ...p[kind], date: e.target.value } }))} data-testid={`cc-${kind}-date-${id}`} />
        <input type="number" placeholder="Amount" className={`${II} tabular-nums`} value={draft[kind].amount} onChange={(e) => setDraft((p) => ({ ...p, [kind]: { ...p[kind], amount: e.target.value } }))} data-testid={`cc-${kind}-amount-${id}`} />
        <input placeholder="Note" className={II} value={draft[kind].note} onChange={(e) => setDraft((p) => ({ ...p, [kind]: { ...p[kind], note: e.target.value } }))} data-testid={`cc-${kind}-note-${id}`} />
        <button onClick={() => addEvent(kind)} disabled={busy} data-testid={`cc-${kind}-add-${id}`} className="flex items-center justify-center gap-1 rounded bg-[var(--primary)] px-2 py-1 text-[12px] font-semibold text-white disabled:opacity-40">{busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}</button>
      </div>
      {list.length === 0 ? <div className="rounded border border-dashed border-black/10 py-2 text-center text-[11px] opacity-55 dark:border-white/10">None logged.</div> : (
        <div className="space-y-1" data-testid={`cc-${kind}-list-${id}`}>
          {[...list].sort((a, b) => String(b.date).localeCompare(String(a.date))).map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 rounded border border-black/5 px-2 py-1 text-[12px] dark:border-white/5" data-testid={`cc-${kind}-row-${e.id}`}>
              <span className="opacity-70">{e.date}</span>
              <span className="tabular-nums font-medium">{money(e.amount)}</span>
              <span className="flex-1 truncate opacity-60">{e.note}</span>
              <button onClick={() => delEvent(kind, e.id)} disabled={busy} data-testid={`cc-${kind}-del-${e.id}`} className="text-black/40 hover:text-red-500 dark:text-white/40"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <button type="button" onClick={openModal} title="Capital commitment & calls" data-testid={`cc-open-${id}`}
        className="inline-flex items-center gap-1 rounded border border-black/15 px-1.5 py-1 text-[11px] hover:bg-black/[0.04] dark:border-white/15 dark:hover:bg-white/[0.06]">
        <PiggyBank size={13} />{commit ? <span className="tabular-nums">{pct}% called</span> : <span className="opacity-70">Commitment</span>}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Capital commitment & calls" testid={`cc-modal-${id}`} wide>
        <div className="mb-3 flex items-end gap-3">
          <label className="block w-56"><span className={LL}>Total commitment ($)</span>
            <input type="number" className={`${II} tabular-nums`} value={commitment} onChange={(e) => setCommitment(e.target.value)} onBlur={saveCommitment} data-testid={`cc-commitment-${id}`} /></label>
        </div>

        <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Called to date" value={money(called)} tone="text-amber-600" />
          <Stat label="Remaining (uncalled)" value={commit ? money(remaining) : "—"} tone="text-[var(--primary)]" />
          <Stat label="Distributed" value={money(distributed)} tone="text-emerald-600" />
          <Stat label="% called" value={commit ? `${pct}%` : "—"} />
        </div>
        <div className="mb-4 h-2 overflow-hidden rounded-full bg-black/10 dark:bg-white/10" data-testid={`cc-bar-${id}`}>
          <div className="h-full rounded-full bg-[var(--primary)] transition-all" style={{ width: `${pct}%` }} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <EventBlock kind="call" list={calls} title="Capital calls (drawdowns)" icon={ArrowDownToLine} tone="text-amber-600" />
          <EventBlock kind="distribution" list={dists} title="Distributions" icon={ArrowUpFromLine} tone="text-emerald-600" />
        </div>
        <p className="mt-3 text-[11px] opacity-55">This is a plain running tally (committed − called = remaining). It is not a reconciliation or waterfall engine and does not pull from a custodian.</p>
      </Modal>
    </>
  );
}
